import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GridViewOutlined,
  TerminalOutlined,
  AutoAwesomeOutlined,
  DownloadOutlined,
  TagOutlined,
  RuleOutlined,
  DnsOutlined,
  AssignmentOutlined,
  WorkOutlineOutlined,
  PersonOutlineOutlined,
  ChecklistOutlined,
  FactCheckOutlined,
} from '@mui/icons-material';
import { Trans, useTranslation } from 'react-i18next';
import styles from './_shared.module.scss';

const WorkBookGuide = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className={styles.page}>
      {/* Hero */}
      <section className={styles.hero}>
        <div className={styles.heroGridOverlay} />
        <div className={styles.heroInner}>
          <div>
            <span className={styles.eyebrow}>Excel Automation</span>
            <h1 className={styles.heroTitle}>
              <span className={styles.brandGradient}>WorkBook</span> Guide
            </h1>
            <p className={styles.heroSubtitle}>
              {t('guide.wb.subtitle')}
            </p>
            <div className={styles.heroActions}>
              <button className={styles.btnPrimary} onClick={() => navigate('/cmdexcel')}>
                {t('guide.wb.cta')}
              </button>
            </div>
          </div>
          <div className={styles.heroBadge}>
            <GridViewOutlined />
          </div>
        </div>
      </section>

      {/* Use cases — 두 페르소나 */}
      <section className={styles.section}>
        <header className={styles.sectionHeader}>
          <span className={styles.sectionEyebrow}>Who uses it</span>
          <h2 className={styles.sectionTitle}>{t('guide.wb.useCaseTitle')}</h2>
          <p className={styles.sectionLead}>
            {t('guide.wb.useCaseLead')}
          </p>
        </header>

        <div className={styles.featureGrid}>
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}><PersonOutlineOutlined /></div>
            <span className={styles.featureTag}>Client</span>
            <h3 className={styles.featureTitle}>{t('guide.wb.clientTitle')}</h3>
            <p className={styles.featureDesc}>
              {t('guide.wb.clientDesc')}
            </p>
            <ul className={styles.featureBullets}>
              <li>{t('guide.wb.clientBullet1')}</li>
              <li>{t('guide.wb.clientBullet2')}</li>
              <li>{t('guide.wb.clientBullet3')}</li>
            </ul>
          </div>

          <div className={styles.featureCard}>
            <div className={styles.featureIcon}><WorkOutlineOutlined /></div>
            <span className={styles.featureTag}>Engineer</span>
            <h3 className={styles.featureTitle}>{t('guide.wb.workerTitle')}</h3>
            <p className={styles.featureDesc}>
              {t('guide.wb.workerDesc')}
            </p>
            <ul className={styles.featureBullets}>
              <li>{t('guide.wb.workerBullet1')}</li>
              <li>{t('guide.wb.workerBullet2')}</li>
              <li>{t('guide.wb.workerBullet3')}</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className={styles.section}>
        <header className={styles.sectionHeader}>
          <span className={styles.sectionEyebrow}>{t('guide.common.howToUse')}</span>
          <h2 className={styles.sectionTitle}>{t('guide.wb.stepsTitle')}</h2>
          <p className={styles.sectionLead}>
            {t('guide.wb.stepsLead')}
          </p>
        </header>

        <div className={styles.stepList}>
          {/* Step 1 */}
          <div className={styles.step}>
            <div className={styles.stepNumber}>1</div>
            <div className={styles.stepBody}>
              <h3 className={styles.stepTitle}>{t('guide.wb.step1Title')}</h3>
              <p className={styles.stepDesc}>
                {t('guide.wb.step1Desc')}
              </p>
              <div className={styles.callout}>
                <strong>{t('guide.wb.step1Id')}</strong>
                <br />
                <Trans i18nKey="guide.wb.step1IdStart" />
                <br />
                <Trans i18nKey="guide.wb.step1IdStep" />
              </div>
              <div className={styles.callout}>
                <strong>{t('guide.wb.step1Match')}</strong>
                <br />
                <Trans i18nKey="guide.wb.step1MatchH" />
                <br />
                <Trans i18nKey="guide.wb.step1MatchF" />
              </div>
              <div className={styles.callout}>
                <Trans i18nKey="guide.wb.step1File" />
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className={styles.step}>
            <div className={styles.stepNumber}>2</div>
            <div className={styles.stepBody}>
              <h3 className={styles.stepTitle}>{t('guide.wb.step2Title')}</h3>
              <p className={styles.stepDesc}>
                {t('guide.wb.step2Desc')}
              </p>
              <div className={styles.callout}>
                <strong>{t('guide.wb.step2Host')}</strong> {t('guide.wb.step2HostCol')}
                <br />
                {t('guide.wb.step2HostDesc')}
                <br />
                <em>{t('guide.wb.step2HostEx')}</em>
              </div>
              <div className={styles.callout}>
                <strong>{t('guide.wb.step2Cmd')}</strong> {t('guide.wb.step2CmdCol')}
                <br />
                {t('guide.wb.step2CmdDesc')}
                <br />
                <em>{t('guide.wb.step2CmdEx')}</em>
              </div>
              <div className={styles.callout}>
                <Trans i18nKey="guide.wb.step2Add" />
                <br />
                <Trans i18nKey="guide.wb.step2Del" />
                <br />
                <Trans i18nKey="guide.wb.step2Rollback" />
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div className={styles.step}>
            <div className={styles.stepNumber}>3</div>
            <div className={styles.stepBody}>
              <h3 className={styles.stepTitle}>{t('guide.wb.step3Title')}</h3>
              <p className={styles.stepDesc}>
                <Trans i18nKey="guide.wb.step3Desc" />
              </p>
              <div className={`${styles.callout} ${styles.calloutBrand}`}>
                <Trans i18nKey="guide.wb.step3Note" />
              </div>
            </div>
          </div>

          {/* Step 4 */}
          <div className={styles.step}>
            <div className={styles.stepNumber}>4</div>
            <div className={styles.stepBody}>
              <h3 className={styles.stepTitle}>{t('guide.wb.step4Title')}</h3>
              <p className={styles.stepDesc}>
                <Trans i18nKey="guide.wb.step4Desc" />
              </p>
              <div className={styles.callout}>
                <strong>{t('guide.wb.step4Fields')}</strong>
                <br />
                <Trans i18nKey="guide.wb.step4Field1" />
                <br />
                <Trans i18nKey="guide.wb.step4Field2" />
                <br />
                <Trans i18nKey="guide.wb.step4Field3" />
                <br />
                <Trans i18nKey="guide.wb.step4Field4" />
                <br />
                <Trans i18nKey="guide.wb.step4Field5" />
              </div>
              <p className={styles.stepDesc}>
                <Trans i18nKey="guide.wb.step4Note" />
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WorkBook 구성 */}
      <section className={styles.section}>
        <header className={styles.sectionHeader}>
          <span className={styles.sectionEyebrow}>WorkBook Contents</span>
          <h2 className={styles.sectionTitle}>{t('guide.wb.contentsTitle')}</h2>
        </header>
        <div className={styles.pillGrid}>
          <div className={styles.pillCard}><DnsOutlined /><span className={styles.pillLabel}>{t('guide.wb.pillSheet')}</span></div>
          <div className={styles.pillCard}><TerminalOutlined /><span className={styles.pillLabel}>{t('guide.wb.pillCmd')}</span></div>
          <div className={styles.pillCard}><AssignmentOutlined /><span className={styles.pillLabel}>{t('guide.wb.pillRollback')}</span></div>
          <div className={styles.pillCard}><TagOutlined /><span className={styles.pillLabel}>{t('guide.wb.pillId')}</span></div>
          <div className={styles.pillCard}><RuleOutlined /><span className={styles.pillLabel}>{t('guide.wb.pillMatch')}</span></div>
          <div className={styles.pillCard}><ChecklistOutlined /><span className={styles.pillLabel}>{t('guide.wb.pillEquip')}</span></div>
        </div>
      </section>

      {/* Concept benefits */}
      <section className={styles.section}>
        <header className={styles.sectionHeader}>
          <span className={styles.sectionEyebrow}>At a Glance</span>
          <h2 className={styles.sectionTitle}>{t('guide.wb.atGlanceTitle')}</h2>
        </header>
        <div className={styles.benefitGrid}>
          <div className={styles.benefitCard}>
            <div className={styles.benefitIcon}><TerminalOutlined /></div>
            <h4 className={styles.benefitTitle}>{t('guide.wb.benefit1Title')}</h4>
            <p className={styles.benefitDesc}>{t('guide.wb.benefit1Desc')}</p>
          </div>
          <div className={styles.benefitCard}>
            <div className={styles.benefitIcon}><AutoAwesomeOutlined /></div>
            <h4 className={styles.benefitTitle}>{t('guide.wb.benefit2Title')}</h4>
            <p className={styles.benefitDesc}>{t('guide.wb.benefit2Desc')}</p>
          </div>
          <div className={styles.benefitCard}>
            <div className={styles.benefitIcon}><FactCheckOutlined /></div>
            <h4 className={styles.benefitTitle}>{t('guide.wb.benefit3Title')}</h4>
            <p className={styles.benefitDesc}>{t('guide.wb.benefit3Desc')}</p>
          </div>
          <div className={styles.benefitCard}>
            <div className={styles.benefitIcon}><DownloadOutlined /></div>
            <h4 className={styles.benefitTitle}>{t('guide.wb.benefit4Title')}</h4>
            <p className={styles.benefitDesc}>{t('guide.wb.benefit4Desc')}</p>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className={styles.closing}>
        <div className={styles.closingBox}>
          <h2>{t('guide.wb.closingTitle')}</h2>
          <p>
            {t('guide.wb.closingDesc')}
          </p>
          <button className={styles.btnPrimary} onClick={() => navigate('/cmdexcel')}>
            {t('guide.wb.closingButton')}
          </button>
        </div>
      </section>
    </div>
  );
};

export default WorkBookGuide;
